/**
 * Session state and the producer role guard.
 *
 * Adapts what `@most/auth` will export (`useMe`, `RoleGuard`, the 401 handler). Kept local so
 * this app is not blocked on that package; the hook names match so the swap is mechanical.
 *
 * Client-side guards are UX ONLY. Authorization is enforced by the backend on every request
 * (docs/06 §3) — a producer who edits `location.pathname` gets a 403/404 from the API, not data.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LOGIN_PATH } from '@most/auth';
import * as api from './api';
import { qk } from './queryClient';
import type { Locale, Me } from './contract';
import { changeLocale } from '@/i18n';

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: api.getMe,
    // The session is the app's spine: a stale `Me` means a stale role guard.
    staleTime: 60_000,
    retry: false,
  });
}

export function useConfig() {
  return useQuery({
    queryKey: qk.config,
    queryFn: api.getConfig,
    // Server constants (truck sizes, upload limits) change on deploy, not during a session.
    staleTime: Infinity,
  });
}

/**
 * Locale is persisted to `users.locale` server-side, not only in localStorage — the backend
 * renders this user's emails and Telegram messages in that language (openapi PATCH /me). A
 * client-only switch would leave the producer reading a Russian UI and receiving English alerts.
 */
export function useChangeLocale() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (locale: Locale) => {
      await changeLocale(locale);
      // Best-effort: if the user is mid-session and the PATCH fails, the UI language still
      // changed locally. Not worth blocking the switcher on.
      return api.updateLocale(locale);
    },
    onSuccess: (me: Me) => {
      qc.setQueryData(qk.me, me);
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: api.logout,
    onSettled: () => {
      // Full reload: drops every cache and every in-memory copy of this producer's data, and
      // lands on the landing app's login screen (another origin, hence not a router push).
      window.location.assign(LOGIN_PATH);
    },
  });
}

/**
 * Producer account gating (docs/06 §3):
 *   • `pending_approval` — PRO-1 and PRO-2 are reachable; PRO-3/PRO-4 render LOCKED with an
 *     explanatory empty state. The producer may still prepare drafts (docs/04 §1.1) but nothing
 *     is buyer-visible regardless, since `buyable` also requires the producer to be `active`.
 *   • `blocked` — the server has already revoked the session; we show a neutral screen.
 *   • `pending_email` — only the verify-email prompt (on the landing app) is reachable.
 */
export function canManageProducts(me: Me | undefined): boolean {
  return me?.status === 'active';
}
